/**
 * Light, dark, or whatever the system says.
 *
 * THREE CHOICES, TWO THEMES. The person chooses "system", "light" or "dark";
 * the page only ever WEARS light or dark. `resolveTheme` is the whole rule,
 * and `public/theme.js` repeats it before the first paint so nobody sees a
 * white flash on the way to a dark page.
 *
 * WHERE THE CHOICE LIVES. In this browser (usePersistentChoice, the same
 * store the layout choice uses, so every control in the tab agrees), and on
 * the person's own member record as `themePreference`, the one field besides
 * the avatar colour the rules let a member write themselves. The record is
 * what makes a shared workstation behave: the night nurse's dark screen does
 * not greet the day nurse, because signing in adopts the choice kept with the
 * account. A failure to read or write that record is never shown: the theme
 * still applies here, it just does not travel.
 */

import { useCallback, useEffect } from 'react';
import { doc, getDoc, getFirestore, serverTimestamp, updateDoc } from 'firebase/firestore';
import { app } from '../lib/firebase';
import { useAuth } from '../auth/useAuth';
import { usePersistentChoice } from '../shared/usePersistentChoice';

export const THEME_CHOICES = ['system', 'light', 'dark'] as const;
export type ThemeChoice = (typeof THEME_CHOICES)[number];
export type Theme = 'light' | 'dark';

/** The storage key, less the store's own prefix. `public/theme.js` reads `somexai.theme`. */
export const THEME_KEY = 'theme';

const DARK_QUERY = '(prefers-color-scheme: dark)';

export function isThemeChoice(value: unknown): value is ThemeChoice {
  return typeof value === 'string' && (THEME_CHOICES as readonly string[]).includes(value);
}

export function resolveTheme(choice: ThemeChoice, systemIsDark: boolean): Theme {
  if (choice === 'system') return systemIsDark ? 'dark' : 'light';
  return choice;
}

function systemIsDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches;
}

export function applyTheme(choice: ThemeChoice): void {
  document.documentElement.setAttribute('data-theme', resolveTheme(choice, systemIsDark()));
}

/** The choice, and a setter that also keeps it with the person's account. */
export function useTheme(): [ThemeChoice, (next: ThemeChoice) => void] {
  const { session } = useAuth();
  const [choice, setLocal] = usePersistentChoice<ThemeChoice>(THEME_KEY, THEME_CHOICES, 'system');
  const uid = session?.uid ?? null;
  const orgId = session?.orgId ?? null;

  const set = useCallback(
    (next: ThemeChoice) => {
      setLocal(next);
      if (!uid || !orgId) return;
      updateDoc(doc(getFirestore(app), 'orgs', orgId, 'members', uid), {
        themePreference: next,
        updatedAt: serverTimestamp(),
      }).catch(() => {
        // Applied here all the same; it just will not follow them elsewhere.
      });
    },
    [setLocal, uid, orgId],
  );

  return [choice, set];
}

/**
 * Mounted once, at the root. Wears the choice, follows the system while the
 * choice is "system", and adopts the account's choice when somebody signs in.
 */
export function ThemeSync(): null {
  const { session } = useAuth();
  const [choice, setLocal] = usePersistentChoice<ThemeChoice>(THEME_KEY, THEME_CHOICES, 'system');
  const uid = session?.uid ?? null;
  const orgId = session?.orgId ?? null;

  useEffect(() => {
    applyTheme(choice);
    if (choice !== 'system' || typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(DARK_QUERY);
    const onChange = () => applyTheme('system');
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [choice]);

  useEffect(() => {
    if (!uid || !orgId) return;
    let stale = false;
    getDoc(doc(getFirestore(app), 'orgs', orgId, 'members', uid))
      .then((snap) => {
        const kept = snap.data()?.themePreference;
        if (!stale && isThemeChoice(kept)) setLocal(kept);
      })
      .catch(() => {
        // The browser's own choice stands.
      });
    return () => {
      stale = true;
    };
    // Once per sign-in, not on every local change: the account is read when
    // somebody arrives, and written (by useTheme) when they choose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, orgId]);

  return null;
}
